import { ElementType, FC, ReactNode } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Label } from '@growtherapy/sprout-ui';

import { BasePaginatedDrawerProps, PageComponentProps, usePaginatedPages } from '..';
import { MeasuresContent } from './MeasuresContent';
import { MeasuresDrawer } from './MeasuresDrawer';
import { MeasureSchemaAndAnswers, PendingMeasuresFormLayout } from './types';

export type PendingMeasuresFormProps = {
  /**
   * The measure pages to render. `undefined` indicates the measures are still
   * loading; an empty array indicates there are no outstanding measures.
   */
  pages: MeasureSchemaAndAnswers[] | undefined;
  /**
   * Renders the per-measure form. Defaults to the shared `MeasuresContent`.
   */
  contentComponent?: FC<PageComponentProps<MeasureSchemaAndAnswers>>;
  /**
   * Renders the submit / navigation actions for each measure. Callers supply a
   * wrapper that wires up the appropriate submission behavior + completion
   * campaign for their use case (waiting-room drawer, soft block, etc.).
   */
  actionsComponent: FC<PageComponentProps<MeasureSchemaAndAnswers>>;
  /**
   * Rendered while `pages` is `undefined` (measures still loading).
   */
  loadingFallback?: ReactNode;
  /**
   * Rendered when `pages` is an empty array (no outstanding measures).
   */
  emptyFallback?: ReactNode;
  /**
   * The page to display initially. Clamped to the available page range so a
   * stale saved index can never point past the last measure.
   */
  initialPageIndex?: number;
  /**
   * Called whenever the current page changes, so callers can persist progress.
   */
  onPageIndexChange?: (pageIndex: number) => void;
  /**
   * How the measures are presented:
   * - `'drawer'` (default): rendered inside the waiting-room measures drawer.
   * - `'page'`: rendered as a full-page flow (e.g. the soft block) with no
   *   drawer chrome, transparent background, and a single page-level scroll.
   */
  layout?: PendingMeasuresFormLayout;
  /**
   * Extra content rendered in the actions footer of the `'page'` layout, e.g. a
   * lower-weight "skip" CTA. Ignored by the `'drawer'` layout.
   */
  footerContent?: ReactNode;
  drawerBodyProps?: BasePaginatedDrawerProps<MeasureSchemaAndAnswers>['drawerBodyProps'];
  headingAs?: ElementType;
};

type PendingMeasuresFormPageProps = {
  pages: MeasureSchemaAndAnswers[];
  initialPageIndex: number;
  contentComponent: FC<PageComponentProps<MeasureSchemaAndAnswers>>;
  actionsComponent: FC<PageComponentProps<MeasureSchemaAndAnswers>>;
  onPageIndexChange?: (pageIndex: number) => void;
  footerContent?: ReactNode;
};

/**
 * Full-page (non-drawer) presentation of the measures. Uses the shared
 * pagination engine so the page/measure stepping behaves identically to the
 * drawer, but renders the measure directly on the page: it fills the screen
 * below the navbar, scrolls as a single page-level region, and pins the actions
 * to a bottom toolbar that stays in view while the measure scrolls.
 */
function PendingMeasuresFormPage({
  pages,
  initialPageIndex,
  contentComponent: ContentComponent,
  actionsComponent: ActionsComponent,
  onPageIndexChange,
  footerContent,
}: PendingMeasuresFormPageProps) {
  const t = useTranslate();
  const { componentProps, contentRef } = usePaginatedPages<MeasureSchemaAndAnswers>({
    pages,
    initialPageIndex,
    onPageIndexChange,
  });

  return (
    <div className='flex min-h-0 w-full flex-1 flex-col'>
      <div ref={contentRef} className='w-full flex-1 overflow-y-auto px-component-100 py-component-150'>
        <div className='mx-auto flex w-full max-w-screen-md flex-col gap-component-100'>
          <Label.Default className='text-neutral-700 rebrand:text-text-subtle'>
            {t('telehealth-core:waitingRoom.questionnaireAofB', {
              index: componentProps.pageIndex + 1,
              total: componentProps.pageCount,
            })}
          </Label.Default>
          <ContentComponent {...componentProps} />
        </div>
      </div>
      <div className='w-full border-t border-neutral-400 bg-neutral-300 rebrand:border-border-subtle rebrand:bg-surface-default'>
        <div className='mx-auto flex w-full max-w-screen-md items-center justify-end gap-component-50 px-component-100 py-component-50'>
          {footerContent}
          <ActionsComponent {...componentProps} />
        </div>
      </div>
    </div>
  );
}

/**
 * Shared, drawer-agnostic component that renders a paginated set of pending
 * measure forms (PHQ-9, GAD-7, etc.) along with their submit / navigation
 * actions. This decouples the measure-rendering experience from the waiting-room
 * drawer so the same form can be reused across surfaces (e.g. the soft-block
 * pre-session flow). Each surface injects its own data source, content/actions
 * components, loading / empty states, and presentation `layout`.
 */
export function PendingMeasuresForm({
  pages,
  contentComponent = MeasuresContent,
  actionsComponent,
  loadingFallback = null,
  emptyFallback = null,
  initialPageIndex = 0,
  onPageIndexChange,
  layout = PendingMeasuresFormLayout.DRAWER,
  footerContent,
  drawerBodyProps,
  headingAs,
}: PendingMeasuresFormProps) {
  // Pages is initially undefined before measures finishes loading
  if (!pages) {
    return <>{loadingFallback}</>;
  }

  // Once loaded, if there are no pages, there are no outstanding measures
  if (!pages.length) {
    return <>{emptyFallback}</>;
  }

  const clampedInitialPageIndex = Math.min(initialPageIndex, pages.length - 1);

  if (layout === PendingMeasuresFormLayout.PAGE) {
    return (
      <PendingMeasuresFormPage
        pages={pages}
        initialPageIndex={clampedInitialPageIndex}
        contentComponent={contentComponent}
        actionsComponent={actionsComponent}
        onPageIndexChange={onPageIndexChange}
        footerContent={footerContent}
      />
    );
  }

  return (
    <MeasuresDrawer
      pages={pages}
      headingAs={headingAs}
      initialPageIndex={clampedInitialPageIndex}
      contentComponent={contentComponent}
      actionsComponent={actionsComponent}
      drawerBodyProps={drawerBodyProps}
      onPageIndexChange={onPageIndexChange}
    />
  );
}
